import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, IconButton, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import BarraSemaforo from './BarraSemaforo';
import StatusChip from './StatusChip';
import {
  COR, STATUS, STATUS_ORDEM, baixarRelatorio, buscarRelatorio, formatarData, formatarPercentual,
  formatarReal, paraISO,
} from './financeiroUtils';

const CAMPOS_DATA = [
  { valor: 'competencia', rotulo: 'Mês de competência da NF' },
  { valor: 'vencimento', rotulo: 'Data de vencimento' },
  { valor: 'recebimento', rotulo: 'Data do recebimento' },
];

const FILTROS_VAZIOS = { campo_data: 'competencia', data_de: '', data_ate: '', projeto: '', cliente: '', status: [] };

// Atalhos de período. Devolvem { data_de, data_ate } em AAAA-MM-DD.
const PERIODOS = [
  { id: 'mes', rotulo: 'Este mês', calcular: (h) => [new Date(h.getFullYear(), h.getMonth(), 1), new Date(h.getFullYear(), h.getMonth() + 1, 0)] },
  { id: 'mes_passado', rotulo: 'Mês passado', calcular: (h) => [new Date(h.getFullYear(), h.getMonth() - 1, 1), new Date(h.getFullYear(), h.getMonth(), 0)] },
  { id: 'ano', rotulo: 'Este ano', calcular: (h) => [new Date(h.getFullYear(), 0, 1), new Date(h.getFullYear(), 11, 31)] },
  { id: '12m', rotulo: 'Últimos 12 meses', calcular: (h) => [new Date(h.getFullYear(), h.getMonth() - 11, 1), new Date(h.getFullYear(), h.getMonth() + 1, 0)] },
  { id: 'tudo', rotulo: 'Todo o período', calcular: () => [null, null] },
];

const numero = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const cabecalho = { fontSize: 12.5, fontWeight: 600, color: COR.texto2, whiteSpace: 'nowrap', py: 1, backgroundColor: COR.fundo };

function Indicador({ rotulo, valor, cor = COR.ink, detalhe }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, color: COR.texto2 }}>{rotulo}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>{valor}</Typography>
      {detalhe && <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>{detalhe}</Typography>}
    </Box>
  );
}

// Gráfico simples de barras (faturado × recebido por mês), sem biblioteca externa.
function GraficoMeses({ meses }) {
  const dados = meses.slice(-18);
  const maximo = Math.max(1, ...dados.map((m) => Math.max(Number(m.faturado), Number(m.recebido))));
  const resumo = `Faturado e recebido por mês: ${dados.map((m) => `${m.label}, ${formatarReal(m.faturado)} faturado e ${formatarReal(m.recebido)} recebido`).join('; ')}.`;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: COR.ink }}>Faturado e recebido por mês</Typography>
        <Box sx={{ display: 'flex', gap: 2, fontSize: 12.5, color: COR.texto2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: '#A9BCD6' }} /> Faturado
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: COR.navy }} /> Recebido (líquido)
          </Box>
        </Box>
      </Box>
      <Box role="img" aria-label={resumo} sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 0.75, sm: 1.5 }, height: 150, borderBottom: `1px solid ${COR.borda}`, overflowX: 'auto', pb: 0 }}>
        {dados.map((m) => (
          <Tooltip key={`${m.ano}-${m.mes}`} arrow title={`${m.label}. Faturado ${formatarReal(m.faturado)}. Recebido ${formatarReal(m.recebido)}.`}>
            <Box sx={{ flex: '1 1 0', minWidth: 28, maxWidth: 110, height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '3px' }}>
              <Box sx={{ width: '42%', height: `${(Number(m.faturado) / maximo) * 100}%`, minHeight: 2, backgroundColor: '#A9BCD6', borderRadius: '3px 3px 0 0' }} />
              <Box sx={{ width: '42%', height: `${(Number(m.recebido) / maximo) * 100}%`, minHeight: Number(m.recebido) ? 2 : 0, backgroundColor: COR.navy, borderRadius: '3px 3px 0 0' }} />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.5 }, overflowX: 'auto' }}>
        {dados.map((m) => (
          <Typography key={`${m.ano}-${m.mes}`} sx={{ flex: '1 1 0', minWidth: 28, maxWidth: 110, textAlign: 'center', fontSize: 11.5, color: COR.texto2, pt: 0.5 }}>
            {m.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default function RelatoriosDialog({ aberto, onFechar, projetos, filtrosIniciais }) {
  const telaPequena = useMediaQuery('(max-width:900px)');
  const [rascunho, setRascunho] = useState(FILTROS_VAZIOS);   // o que está nos campos
  const [aplicado, setAplicado] = useState(FILTROS_VAZIOS);   // o que gerou o resultado na tela
  const [periodo, setPeriodo] = useState('tudo');
  const [relatorio, setRelatorio] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [baixando, setBaixando] = useState('');
  const [erro, setErro] = useState('');
  const sequencia = useRef(0);

  const clientes = useMemo(() => {
    const mapa = new Map();
    (projetos || []).forEach((p) => { if (p.cliente) mapa.set(p.cliente, p.cliente_nome); });
    return [...mapa.entries()].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [projetos]);

  const listaProjetos = useMemo(
    () => [...(projetos || [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [projetos],
  );

  const gerar = useCallback(async (filtros) => {
    const minha = ++sequencia.current;
    setCarregando(true);
    setErro('');
    try {
      const dados = await buscarRelatorio(filtros);
      if (minha !== sequencia.current) return;   // chegou resposta antiga, ignora
      setRelatorio(dados);
      setAplicado(filtros);
    } catch (err) {
      if (minha !== sequencia.current) return;
      setErro(err?.response?.data?.detail || 'Não foi possível gerar o relatório.');
    } finally {
      if (minha === sequencia.current) setCarregando(false);
    }
  }, []);

  // Ao abrir, já gera o relatório (com o filtro que veio da tela, se houver).
  useEffect(() => {
    if (!aberto) return;
    const iniciais = { ...FILTROS_VAZIOS, ...(filtrosIniciais || {}) };
    setRascunho(iniciais);
    setPeriodo('tudo');
    setRelatorio(null);
    gerar(iniciais);
  }, [aberto, filtrosIniciais, gerar]);

  const escolherPeriodo = (p) => {
    const [de, ate] = p.calcular(new Date());
    setPeriodo(p.id);
    setRascunho((f) => ({ ...f, data_de: de ? paraISO(de) : '', data_ate: ate ? paraISO(ate) : '' }));
  };

  const alternarStatus = (s) => setRascunho((f) => ({
    ...f, status: f.status.includes(s) ? f.status.filter((x) => x !== s) : [...f.status, s],
  }));

  const baixar = async (formato) => {
    setBaixando(formato);
    setErro('');
    try {
      await baixarRelatorio(formato, aplicado);
    } catch (err) {
      setErro(err.message);
    } finally {
      setBaixando('');
    }
  };

  const filtrosAlterados = JSON.stringify(rascunho) !== JSON.stringify(aplicado);
  const r = relatorio?.resumo;
  const valoresStatus = relatorio
    ? Object.fromEntries(relatorio.por_status.map((s) => [s.status, Number(s.valor)]))
    : {};

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="xl" fullWidth fullScreen={telaPequena} scroll="paper" slotProps={{ paper: { sx: { '& .MuiButton-root': { textTransform: 'none', fontWeight: 600 } } } }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2, borderBottom: `1px solid ${COR.borda}`, position: 'relative' }}>
        <IconButton aria-label="Fechar" onClick={onFechar} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
        <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700, color: COR.ink, pr: 6 }}>
          Relatórios de faturamento e recebimento
        </Typography>
        <Typography sx={{ fontSize: 14, color: COR.texto2 }}>
          Escolha o período e os filtros, confira o resultado na tela e baixe em Excel ou PDF.
        </Typography>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {/* ---------------------------------------------------------- filtros */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, backgroundColor: COR.fundo, borderBottom: `1px solid ${COR.borda}` }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {PERIODOS.map((p) => (
              <Chip
                key={p.id} label={p.rotulo} clickable onClick={() => escolherPeriodo(p)}
                sx={{
                  fontWeight: 600, backgroundColor: periodo === p.id ? COR.ink : 'white', color: periodo === p.id ? 'white' : COR.ink,
                  border: `1px solid ${periodo === p.id ? COR.ink : '#d5dbe3'}`,
                  '&:hover': { backgroundColor: periodo === p.id ? COR.ink : '#eef1f5' },
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' } }}>
            <TextField select size="small" label="Período por" value={rascunho.campo_data} onChange={(e) => setRascunho((f) => ({ ...f, campo_data: e.target.value }))} sx={{ backgroundColor: 'white' }}>
              {CAMPOS_DATA.map((c) => <MenuItem key={c.valor} value={c.valor}>{c.rotulo}</MenuItem>)}
            </TextField>
            <TextField
              size="small" type="date" label="De" value={rascunho.data_de} sx={{ backgroundColor: 'white' }}
              onChange={(e) => { setPeriodo(''); setRascunho((f) => ({ ...f, data_de: e.target.value })); }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small" type="date" label="Até" value={rascunho.data_ate} sx={{ backgroundColor: 'white' }}
              onChange={(e) => { setPeriodo(''); setRascunho((f) => ({ ...f, data_ate: e.target.value })); }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField select size="small" label="Projeto ou contrato" value={rascunho.projeto} onChange={(e) => setRascunho((f) => ({ ...f, projeto: e.target.value }))} sx={{ backgroundColor: 'white' }} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
              <MenuItem value="">Todos os projetos e contratos</MenuItem>
              {listaProjetos.map((p) => <MenuItem key={p.chave || p.id} value={p.chave || p.id}>{p.eh_contrato ? `Contrato: ${p.nome}` : p.nome}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Cliente" value={rascunho.cliente} onChange={(e) => setRascunho((f) => ({ ...f, cliente: e.target.value }))} sx={{ backgroundColor: 'white' }} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
              <MenuItem value="">Todos os clientes</MenuItem>
              {clientes.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Typography sx={{ fontSize: 13, color: COR.texto2, mr: 0.5 }}>Situação:</Typography>
            {STATUS_ORDEM.map((s) => {
              const ativo = rascunho.status.includes(s);
              return (
                <Chip
                  key={s} clickable onClick={() => alternarStatus(s)} aria-pressed={ativo}
                  label={STATUS[s].label}
                  icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ativo ? 'white' : STATUS[s].cor, ml: '10px !important' }} />}
                  sx={{
                    fontWeight: 600, backgroundColor: ativo ? STATUS[s].cor : 'white', color: ativo ? 'white' : STATUS[s].texto,
                    border: `1px solid ${ativo ? STATUS[s].cor : '#d5dbe3'}`,
                    '&:hover': { backgroundColor: ativo ? STATUS[s].cor : STATUS[s].fundo },
                  }}
                />
              );
            })}
            {rascunho.status.length === 0 && <Typography sx={{ fontSize: 12.5, color: COR.texto2 }}>Nenhuma marcada: mostra todas.</Typography>}

            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained" onClick={() => gerar(rascunho)} disabled={carregando}
              sx={{ backgroundColor: COR.navy, '&:hover': { backgroundColor: COR.ink } }}
            >
              {carregando ? 'Gerando...' : filtrosAlterados ? 'Gerar com estes filtros' : 'Atualizar'}
            </Button>
          </Box>
        </Box>

        {/* --------------------------------------------------------- resultado */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

          {!relatorio && carregando && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          )}

          {relatorio && (
            <Box sx={{ opacity: carregando ? 0.5 : 1, transition: 'opacity 150ms ease' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                <Typography sx={{ fontSize: 14, color: COR.texto2 }}>
                  {r.qtd_nfs} {r.qtd_nfs === 1 ? 'nota encontrada' : 'notas encontradas'}
                  {relatorio.filtros.data_de || relatorio.filtros.data_ate
                    ? `, de ${formatarData(relatorio.filtros.data_de) || 'início'} até ${formatarData(relatorio.filtros.data_ate) || 'hoje'}`
                    : ', em todo o período'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined" startIcon={baixando === 'xlsx' ? <CircularProgress size={16} /> : <TableViewOutlinedIcon />}
                    onClick={() => baixar('xlsx')} disabled={Boolean(baixando) || carregando || r.qtd_nfs === 0}
                    sx={{ color: '#1D6F42', borderColor: '#1D6F42', '&:hover': { borderColor: '#1D6F42', backgroundColor: '#EAF5EE' } }}
                  >
                    Baixar Excel
                  </Button>
                  <Button
                    variant="outlined" startIcon={baixando === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfOutlinedIcon />}
                    onClick={() => baixar('pdf')} disabled={Boolean(baixando) || carregando || r.qtd_nfs === 0}
                    sx={{ color: COR.destaque, borderColor: COR.destaque, '&:hover': { borderColor: COR.destaque, backgroundColor: '#FFF1F3' } }}
                  >
                    Baixar PDF
                  </Button>
                </Box>
              </Box>

              {filtrosAlterados && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Você mudou os filtros. Clique em Gerar com estes filtros para atualizar o resultado e os arquivos.
                </Alert>
              )}

              {r.qtd_nfs === 0 ? (
                <Box sx={{ textAlign: 'center', py: 7, color: COR.texto2 }}>
                  <Typography sx={{ fontWeight: 700, color: COR.ink, mb: 0.5 }}>Nenhuma nota encontrada</Typography>
                  <Typography sx={{ fontSize: 14 }}>Amplie o período ou remova algum filtro para ver resultados.</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'grid', gap: { xs: 2, md: 4 }, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, auto)' }, justifyContent: 'start', mb: 2.5 }}>
                    <Indicador rotulo="Faturado" valor={formatarReal(r.total_faturado)} />
                    <Indicador rotulo="Recebido (líquido)" valor={formatarReal(r.total_recebido)} cor={COR.navy} detalhe={`${formatarPercentual(r.pct_recebido)} do faturado quitado`} />
                    <Indicador rotulo="Em aberto" valor={formatarReal(r.saldo_em_aberto)} />
                    <Indicador rotulo="Vencido" valor={formatarReal(r.total_vencido)} cor={Number(r.total_vencido) ? STATUS.em_aberto.texto : COR.ink} />
                    <Indicador rotulo="Diferença entre NF e líquido" valor={formatarReal(r.diferenca_nf_liquido)} detalhe="Retenções e descontos" />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <BarraSemaforo valores={valoresStatus} altura={12} animar={false} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 3 }, mt: 1.25 }}>
                      {relatorio.por_status.filter((s) => s.qtd > 0).map((s) => (
                        <Box key={s.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13, color: COR.ink }}>
                          <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: STATUS[s.status].cor }} />
                          <b>{s.label}</b> {s.qtd} {s.qtd === 1 ? 'nota' : 'notas'}, {formatarReal(s.valor)}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3.5 }}><GraficoMeses meses={relatorio.por_competencia} /></Box>

                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: COR.ink, mb: 1 }}>Por projeto ou contrato</Typography>
                  <TableContainer sx={{ border: `1px solid ${COR.borda}`, borderRadius: 2, mb: 3.5 }}>
                    <Table size="small" sx={{ minWidth: 760 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={cabecalho}>Projeto / contrato</TableCell>
                          <TableCell sx={cabecalho}>Cliente</TableCell>
                          <TableCell sx={cabecalho} align="right">Notas</TableCell>
                          <TableCell sx={cabecalho} align="right">Faturado</TableCell>
                          <TableCell sx={cabecalho} align="right">Recebido (líquido)</TableCell>
                          <TableCell sx={cabecalho} align="right">Em aberto</TableCell>
                          <TableCell sx={cabecalho} align="right">Vencido</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relatorio.por_projeto.map((p) => (
                          <TableRow key={p.projeto_id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ fontWeight: 600 }}>{p.projeto}</TableCell>
                            <TableCell sx={{ color: COR.texto2 }}>{p.cliente}</TableCell>
                            <TableCell align="right" sx={numero}>{p.qtd}</TableCell>
                            <TableCell align="right" sx={numero}>{formatarReal(p.faturado)}</TableCell>
                            <TableCell align="right" sx={numero}>{formatarReal(p.recebido)}</TableCell>
                            <TableCell align="right" sx={numero}>{formatarReal(p.em_aberto)}</TableCell>
                            <TableCell align="right" sx={{ ...numero, color: Number(p.vencido) ? STATUS.em_aberto.texto : COR.texto2, fontWeight: Number(p.vencido) ? 700 : 400 }}>
                              {formatarReal(p.vencido)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: COR.ink, mb: 1 }}>Lançamentos</Typography>
                  <TableContainer sx={{ border: `1px solid ${COR.borda}`, borderRadius: 2, maxHeight: 460 }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1000 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={cabecalho}>Competência</TableCell>
                          <TableCell sx={cabecalho}>Projeto / contrato</TableCell>
                          <TableCell sx={cabecalho}>NF</TableCell>
                          <TableCell sx={cabecalho}>Parcela</TableCell>
                          <TableCell sx={cabecalho} align="right">Valor da NF</TableCell>
                          <TableCell sx={cabecalho} align="right">Líquido recebido</TableCell>
                          <TableCell sx={cabecalho}>Vencimento</TableCell>
                          <TableCell sx={cabecalho}>Recebimento</TableCell>
                          <TableCell sx={cabecalho}>Situação</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relatorio.linhas.map((l) => (
                          <TableRow key={l.id} sx={{ backgroundColor: l.status === 'em_aberto' ? '#FFF6F6' : 'transparent' }}>
                            <TableCell sx={{ ...numero, fontWeight: 600 }}>{l.competencia}</TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{l.projeto}</Typography>
                              <Typography sx={{ fontSize: 12, color: COR.texto2 }}>{l.cliente}</Typography>
                            </TableCell>
                            <TableCell sx={numero}>{l.numero_nf || '—'}</TableCell>
                            <TableCell sx={numero}>{l.parcela || '—'}</TableCell>
                            <TableCell align="right" sx={{ ...numero, fontWeight: 600 }}>{formatarReal(l.valor_nf)}</TableCell>
                            <TableCell align="right" sx={numero}>{l.valor_liquido_recebido !== null ? formatarReal(l.valor_liquido_recebido) : '—'}</TableCell>
                            <TableCell sx={numero}>{formatarData(l.data_vencimento) || '—'}</TableCell>
                            <TableCell sx={numero}>{formatarData(l.data_recebimento) || '—'}</TableCell>
                            <TableCell><StatusChip status={l.status} pequeno /></TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${COR.navy}`, backgroundColor: '#E3ECF6' } }}>
                          <TableCell colSpan={4}>Total</TableCell>
                          <TableCell align="right" sx={numero}>{formatarReal(r.total_faturado)}</TableCell>
                          <TableCell align="right" sx={numero}>{formatarReal(r.total_recebido)}</TableCell>
                          <TableCell colSpan={3} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
